// Go to definition: where the name at the cursor was declared, answered by the
// native analysis handle's `definition(offset)`. New for Studio - the
// TypeScript package never offered it. Works across `import`: a name brought
// in from another script answers that script's own path and the name's range
// in *that* script's text, so the editor can open the sibling and put the
// caret on the declaration.
//
// The native answer is `null` for a keyword, a prelude name, a field or method
// name after a `.`, and text inside a comment or a string, so a Ctrl-click on
// any of those does nothing rather than jumping somewhere wrong. Returns plain
// data - see `tokenize.ts`'s own doc comment for why no `monaco-editor` import
// belongs in this directory.

import type { NativeModules } from '../native/hollowscript.js';
import type { StudioCapabilityName } from '../capabilityNames.js';
import { DEFAULT_ENTRY_PATH, withAnalysis } from './analysis.js';
import { utf16RangeOf, type TextRange } from './positions.js';

export interface HollowScriptDefinition extends TextRange {
  /** The declaring module's canonical path: `entryPath` for a name declared
   * in the script being edited, otherwise a key of `modules`. */
  readonly file: string;
  /** Whether `file` is the script being edited. */
  readonly inEntry: boolean;
}

export interface HollowScriptDefinitionParams {
  readonly source: string;
  /** A UTF-16 offset into `source` (Monaco's `model.getOffsetAt`). */
  readonly offset: number;
  readonly entryPath?: string;
  readonly grantedCapabilities: ReadonlySet<StudioCapabilityName> | undefined;
  readonly modules?: NativeModules;
  readonly key?: string;
}

/** The declaration of the name at `params.offset`, or `null` where there is
 * none to go to. */
export async function computeHollowScriptDefinition(params: HollowScriptDefinitionParams): Promise<HollowScriptDefinition | null> {
  const entryPath = params.entryPath ?? DEFAULT_ENTRY_PATH;
  const found = await withAnalysis(
    { source: params.source, entryPath, grantedCapabilities: params.grantedCapabilities, modules: params.modules, key: params.key },
    (analysis) => analysis.definition(params.offset),
  );
  if (!found) return null;
  const inEntry = found.file === entryPath;
  const text = inEntry ? params.source : params.modules?.[found.file];
  // A definition in a module the caller did not supply cannot be turned into
  // a position; the native build only answers modules it was given, so this
  // is defensive, not an expected path.
  if (text === undefined) return null;
  return { file: found.file, inEntry, ...utf16RangeOf(text, found.start, found.end) };
}
